import React, { useState, useEffect, useRef, useContext } from "react";
import { format, parse } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import InputMask from "react-input-mask";
import { toast } from "react-toastify";
import XTwitterIcon from "../../assets/x-twitter.png";
import isEqual from "lodash/isEqual";


import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  CircularProgress,
  Switch,
  Tabs,
  Tab,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Avatar,
  Divider,
  Grid,
  InputAdornment,
  Chip
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import ClearIcon from "@material-ui/icons/Clear";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import TwitterIcon from "@material-ui/icons/Twitter";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import LockIcon from "@material-ui/icons/Lock";
import WarningIcon from "@material-ui/icons/Warning";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexWrap: "wrap" },
  textField: { marginBottom: theme.spacing(1), width: "100%" },
  extraAttr: { display: "flex", alignItems: "center" },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: theme.palette.success.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  tabPanel: { padding: theme.spacing(2) },
  avatar: { width: 80, height: 80 }
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

const formatCpfCnpj = (value, documentType) => {
  if (!value) return "";
  const cleaned = value.replace(/\D/g, "");

  if (documentType === "CPF") {
    return cleaned
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  }

  if (documentType === "CNPJ") {
    return cleaned
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }

  return value;
};

const ContactSchema = Yup.object().shape({
  email: Yup.string().email("Email inválido").nullable(),

  gender: Yup.string()
    .nullable(true) // ✅ Permite valor null
    .oneOf(["", "M", "F", "O"], "Selecione um sexo válido"),

  birthDate: Yup.string()
    .nullable()
    .test("is-valid-date", "Data inválida", function (value) {
      if (!value) return true;
      const regex = /^\d{2}\/\d{2}\/\d{4}$/;
      return regex.test(value);
    }),

  documentType: Yup.string().nullable().oneOf(["CPF", "CNPJ", ""]).notRequired(),

  cpfCnpj: Yup.string()
    .nullable()
    .test("cpf-cnpj", "CPF/CNPJ inválido", function (value) {
      const docType = this.parent.documentType;
      if (!docType || !value || value.trim() === "") return true;
      if (docType === "CPF") return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value);
      if (docType === "CNPJ") return /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value);
      return true;
    })
});


const ContactModalNew = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [disableBot, setDisableBot] = useState(false);
  const [users, setUsers] = useState([]);
  const [confirmWalletChangeOpen, setConfirmWalletChangeOpen] = useState(false);
  const [pendingWalletChange, setPendingWalletChange] = useState(null);
  const { user } = useContext(AuthContext);

  const initialState = {
    name: "",
    number: "",
    email: "",
    gender: "",
    birthDate: "",
    profession: "",
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    street: "",
    numberHouse: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    zipCode: "",
    photoUrl: "",
    lgpdAcceptedAt: "",
    extraInfo: [],
    documentType: "CPF",
    cpfCnpj: "",
    wallets: []
  };

  const [contact, setContact] = useState(initialState);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data || []);
      } catch (err) {
        console.error("Erro ao buscar usuários:", err);
      }
    };
    
    if (open) {
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact(prev => ({
          ...prev,
          ...initialValues,
          gender: initialValues.gender || "", // <-- aqui forçamos valor inicial válido
        }));
      } else {
        setContact(prev => ({
          ...prev,
          gender: "", // <-- garante que não vem como undefined ao criar
        }));
      }

      if (!contactId) return;

      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (isMounted.current) {
          setContact({ 
            ...data, 
            birthDate: data.birthDate ? format(new Date(data.birthDate), "dd/MM/yyyy") : "", 
            documentType: data.documentType || "CPF",
            wallets: data.wallets || []
          });
          setDisableBot(data.disableBot);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = (updatedContact = null) => {
    if (updatedContact) {
      onClose(updatedContact); // Se tiver novo contato, envia atualizado
    } else {
      onClose(); // Se não, apenas fecha normal
    }
    setContact(initialState);
  };
  

  const handleSaveContact = async (values, actions) => {
    const payload = {
      ...values,
      gender: values.gender || null,
      birthDate: values.birthDate
        ? parse(values.birthDate, "dd/MM/yyyy", new Date())
        : null,
      disableBot
    };
  
    try {
      if (contactId) {
        await api.put(`/contacts/${contactId}`, payload);
        const { data: updatedContact } = await api.get(`/contacts/${contactId}`);
        toast.success(i18n.t("contactModal.success"));
        handleClose(updatedContact);
      } else {
        const { data: createdContact } = await api.post("/contacts", payload);
        if (onSave) onSave(createdContact);
        toast.success(i18n.t("contactModal.success"));
        handleClose(createdContact);
      }
    } catch (err) {
      toastError(err);
    } finally {
      actions.setSubmitting(false); // ✅ Libera o botão só no final (seja sucesso ou erro)
    }
  };
  
  


  const handleTabChange = (_, newValue) => setTabIndex(newValue);

  const commonTextFieldProps = {
    variant: "outlined",
    margin: "dense",
    className: classes.textField,
    InputLabelProps: { shrink: true }
  };

  // Função para verificar permissões de edição de carteira
  const canEditWallet = (currentWallet) => {
    // Admins e super podem sempre editar
    if (user.profile === "admin" || user.profile === "super") {
      return true;
    }
    
    // O próprio usuário que está vinculado à carteira pode editar
    const currentWalletUserId = currentWallet?.id;
    if (currentWalletUserId && currentWalletUserId === user.id) {
      return true;
    }
    
    return false;
  };

  // Tooltip dinâmico baseado na permissão
  const getWalletTooltipText = (currentWallet) => {
    if (canEditWallet(currentWallet)) {
      return "Selecione um atendente para vincular a este contato";
    }
    
    const isOwnWallet = currentWallet?.id === user.id;
    if (isOwnWallet) {
      return "Apenas você ou um administrador pode alterar esta carteira";
    }
    
    return "Apenas o usuário proprietário ou um administrador pode alterar esta carteira";
  };

  // Funções para confirmação de alteração de carteira
  const handleWalletChangeRequest = (newValue, currentWallet, setFieldValue) => {
    if (!canEditWallet(currentWallet)) return;
    
    setPendingWalletChange({
      newValue,
      currentWallet,
      setFieldValue,
      contactName: contact.name
    });
    setConfirmWalletChangeOpen(true);
  };

  const handleConfirmWalletChange = () => {
    if (pendingWalletChange) {
      pendingWalletChange.setFieldValue("wallets", pendingWalletChange.newValue ? [pendingWalletChange.newValue] : []);
    }
    setConfirmWalletChangeOpen(false);
    setPendingWalletChange(null);
  };

  const handleCancelWalletChange = () => {
    setConfirmWalletChangeOpen(false);
    setPendingWalletChange(null);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} scroll="paper"
        PaperProps={{ style: { width: "100%", maxWidth: 860, height: "90vh", margin: "16px auto" } }}>
        <DialogTitle>
          {contactId
            ? i18n.t("contactModal.title.edit")
            : i18n.t("contactModal.title.add")}
        </DialogTitle>

      <Formik
        initialValues={{
          ...contact,
          gender: contact.gender ?? "", // ✅ Evita null (usa string vazia)
        }}

        enableReinitialize
        validationSchema={ContactSchema}
        onSubmit={async (values, actions) => {
          const filteredInitial = { ...initialValues };
          delete filteredInitial.id;
          delete filteredInitial.createdAt;
          delete filteredInitial.updatedAt;
          delete filteredInitial.companyId;
        
          const filteredValues = { ...values };
          delete filteredValues.id;
          delete filteredValues.createdAt;
          delete filteredValues.updatedAt;
          delete filteredValues.companyId;
        
          if (!isEqual(filteredInitial, filteredValues)) {
            await handleSaveContact(values, actions); // ✅ Passa actions para controlar loading
          } else {
            toast.info("Nenhuma alteração detectada.");
            actions.setSubmitting(false); // ✅ Se nada mudou, libera manualmente
          }
        }}
        
        
      >

        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth">
                <Tab label="Dados do Contato" />
                <Tab label="Outras Opções" />
              </Tabs>

              <TabPanel value={tabIndex} index={0} className={classes.tabPanel}>
                {/* Nome e imagem */}
                <Grid container spacing={2} alignItems="center" justifyContent="center">
                  <Grid item><Avatar src={contact?.profilePicUrl} className={classes.avatar} /></Grid>
                  <Grid item xs={10} sm={8}>
                    <Field as={TextField} name="name" label="Nome" {...commonTextFieldProps} />
                  </Grid>
                </Grid>

                {/* Telefone e Email */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field as={TextField} name="number" label="Telefone" {...commonTextFieldProps} />
                  </Grid>
                  <Grid item xs={6}>
                    <Field as={TextField} name="email" label="Email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      {...commonTextFieldProps} />
                  </Grid>
                </Grid>

                {/* Nascimento e sexo */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InputMask
                      mask="99/99/9999"
                      value={values.birthDate}
                      onChange={(e) => setFieldValue("birthDate", e.target.value)}
                    >
                      {() => (
                        <TextField
                          label="Data de Nascimento"
                          error={touched.birthDate && Boolean(errors.birthDate)}
                          helperText={touched.birthDate && errors.birthDate}
                          InputProps={{
                            endAdornment: values.birthDate && (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setFieldValue("birthDate", "")} size="small">
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          {...commonTextFieldProps}
                        />
                      )}
                    </InputMask>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      name="gender"
                      label="Sexo"
                      value={values.gender || ""}
                      onChange={(e) => setFieldValue("gender", e.target.value)}
                      error={touched.gender && Boolean(errors.gender)}
                      helperText={touched.gender && errors.gender}
                      {...commonTextFieldProps}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Selecione</option>
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                      <option value="O">Outro</option>
                    </TextField>


                  </Grid>
                </Grid>

                {/* Linha 4: Profissão */}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Profissão"
                      name="profession"
                      error={touched.profession && Boolean(errors.profession)}
                      helperText={touched.profession && errors.profession}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>

                <Box my={2}>
                  <Divider />
                </Box>

                {/* Redes Sociais */}
                <Typography variant="subtitle1" gutterBottom>
                  Redes Sociais
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label="Facebook"
                      name="facebook"
                      InputProps={{
                        startAdornment: <FacebookIcon style={{ marginRight: 8 }} />,
                      }}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label="Instagram"
                      name="instagram"
                      InputProps={{
                        startAdornment: <InstagramIcon style={{ marginRight: 8 }} />,
                      }}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label="X"
                      name="twitter"
                      InputProps={{
                        startAdornment: <img
                          src={XTwitterIcon}
                          alt="X"
                          width="20"
                          height="20"
                          style={{ marginRight: 8 }}
                        />
                        ,
                      }}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label="LinkedIn"
                      name="linkedin"
                      InputProps={{
                        startAdornment: <LinkedInIcon style={{ marginRight: 8 }} />,
                      }}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>

                <Box my={2}>
                  <Divider />
                </Box>

                {/* Endereço */}
                <Typography variant="subtitle1" gutterBottom>
                  Endereço
                </Typography>
                {/* Linha 1: Rua e Número (imóvel) */}
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <Field
                      as={TextField}
                      label="Rua"
                      name="street"
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Field
                      as={TextField}
                      label="Número"
                      name="numberHouse"
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
                {/* Linha 2: Complemento e Bairro */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label="Complemento"
                      name="complement"
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      label="Bairro"
                      name="neighborhood"
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
                {/* Linha 3: Cidade, Estado e CEP */}
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Field
                      as={TextField}
                      label="Cidade"
                      name="city"
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Field
                      as={TextField}
                      label="Estado"
                      name="state"
                      select
                      SelectProps={{ native: true }}
                      {...commonTextFieldProps}
                    >
                      <option value="">Selecione</option>
                      <option value="AC">AC</option>
                      <option value="AL">AL</option>
                      <option value="AP">AP</option>
                      <option value="AM">AM</option>
                      <option value="BA">BA</option>
                      <option value="CE">CE</option>
                      <option value="DF">DF</option>
                      <option value="ES">ES</option>
                      <option value="GO">GO</option>
                      <option value="MA">MA</option>
                      <option value="MT">MT</option>
                      <option value="MS">MS</option>
                      <option value="MG">MG</option>
                      <option value="PA">PA</option>
                      <option value="PB">PB</option>
                      <option value="PR">PR</option>
                      <option value="PE">PE</option>
                      <option value="PI">PI</option>
                      <option value="RJ">RJ</option>
                      <option value="RN">RN</option>
                      <option value="RS">RS</option>
                      <option value="RO">RO</option>
                      <option value="RR">RR</option>
                      <option value="SC">SC</option>
                      <option value="SP">SP</option>
                      <option value="SE">SE</option>
                      <option value="TO">TO</option>
                    </Field>

                  </Grid>
                  <Grid item xs={3}>
                    <Field
                      as={TextField}
                      label="CEP"
                      name="zipCode"
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Aba 2: Outras Opções */}
              <TabPanel value={tabIndex} index={1} className={classes.tabPanel}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" style={{ marginBottom: 8 }}>
                      <FormLabel component="legend">Tipo de Documento</FormLabel>
                      <RadioGroup
                        row
                        name="documentType"
                        value={values.documentType || "CPF"} // ✅ Garante CPF como padrão
                        onChange={event => setFieldValue("documentType", event.target.value)}
                      >
                        <FormControlLabel value="CPF" control={<Radio />} label="CPF" />
                        <FormControlLabel value="CNPJ" control={<Radio />} label="CNPJ" />
                      </RadioGroup>


                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={values.documentType === "CPF" ? "CPF" : "CNPJ"}
                      name="cpfCnpj"
                      error={touched.cpfCnpj && Boolean(errors.cpfCnpj)}
                      helperText={touched.cpfCnpj && errors.cpfCnpj}
                      value={values.cpfCnpj} // Mantém o valor formatado
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\D/g, ""); // Remove caracteres não numéricos
                        setFieldValue("cpfCnpj", rawValue);
                      }}
                      onBlur={(e) => {
                        const formattedValue = formatCpfCnpj(e.target.value, values.documentType);
                        setFieldValue("cpfCnpj", formattedValue);
                      }}
                      {...commonTextFieldProps}
                    />

                  </Grid>
                </Grid>

                <Typography variant="subtitle1" gutterBottom>
                  Carteira de clientes
                </Typography>
                
                {/* Campo para seleção de Atendente (Wallet) */}
                <Box mb={2} sx={{ position: "relative" }}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => option.name || ""}
                    value={values.wallets?.[0] || null}
                    disabled={!canEditWallet(values.wallets?.[0])}
                    onChange={(_, newValue) => {
                      if (canEditWallet(values.wallets?.[0])) {
                        // Se for a mesma pessoa, não precisa confirmar
                        if (newValue?.id === values.wallets?.[0]?.id) {
                          setFieldValue("wallets", newValue ? [newValue] : []);
                        } else {
                          // Se for alteração, solicita confirmação
                          handleWalletChangeRequest(newValue, values.wallets?.[0], setFieldValue);
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Atendente Vinculado"
                        placeholder={canEditWallet(values.wallets?.[0]) ? "Selecione um atendente" : "Sem permissão para alterar"}
                        variant="outlined"
                        margin="dense"
                        disabled={!canEditWallet(values.wallets?.[0])}
                        helperText={canEditWallet(values.wallets?.[0]) 
                          ? "Vincule este contato a um atendente específico para direcionar um atendimento diretamente para o usuário."
                          : getWalletTooltipText(values.wallets?.[0])
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              {!canEditWallet(values.wallets?.[0]) && (
                                <InputAdornment position="end">
                                  <LockIcon style={{ color: "#666", marginRight: 8 }} />
                                </InputAdornment>
                              )}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
                
                <div>
                  <TagsContainer contact={contact} className={classes.textField} />
                </div>
                <Typography style={{ marginBottom: 8, marginTop: 12 }} variant="subtitle1">
                  <Switch
                    size="small"
                    checked={disableBot}
                    onChange={() => setDisableBot(!disableBot)}
                    name="disableBot"
                  />
                  {i18n.t("contactModal.form.chatBotContact")}
                </Typography>
                <Typography style={{ marginBottom: 8, marginTop: 12 }} variant="subtitle1">
                  {i18n.t("contactModal.form.termsLGDP")}{" "}
                  {contact?.lgpdAcceptedAt
                    ? format(new Date(contact.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm")
                    : ""}
                </Typography>

                <FieldArray name="extraInfo">
                  {({ push, remove }) => (
                    <>
                      {values.extraInfo &&
                        values.extraInfo.length > 0 &&
                        values.extraInfo.map((info, index) => (
                          <div className={classes.extraAttr} key={`${index}-info`}>
                            <Field
                              as={TextField}
                              label={i18n.t("contactModal.form.extraName")}
                              name={`extraInfo[${index}].name`}
                              {...commonTextFieldProps}
                            />
                            <Field
                              as={TextField}
                              label={i18n.t("contactModal.form.extraValue")}
                              name={`extraInfo[${index}].value`}
                              {...commonTextFieldProps}
                            />
                            <IconButton size="small" onClick={() => remove(index)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </div>
                        ))}
                      <div className={classes.extraAttr}>
                        <Button
                          style={{ flex: 1, marginTop: 8 }}
                          variant="outlined"
                          color="primary"
                          onClick={() => push({ name: "", value: "" })}
                        >
                          {`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
                        </Button>
                      </div>
                    </>
                  )}
                </FieldArray>
              </TabPanel>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                {i18n.t("contactModal.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={classes.btnWrapper}
              >
                {contactId
                  ? i18n.t("contactModal.buttons.okEdit")
                  : i18n.t("contactModal.buttons.okAdd")}
                {isSubmitting && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
      </Dialog>

      {/* Modal de Confirmação de Alteração de Carteira */}
      <Dialog
        open={confirmWalletChangeOpen}
        onClose={handleCancelWalletChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WarningIcon style={{ color: "#ff9800" }} />
          Confirmar Alteração de Carteira
        </DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <Typography variant="body1" style={{ marginBottom: 8, fontWeight: 500 }}>
              Você está alterando a carteira de:
            </Typography>
            <Box style={{ 
              backgroundColor: "#f5f5f5", 
              padding: 12, 
              borderRadius: 4, 
              display: "flex", 
              alignItems: "center",
              gap: 8,
              marginBottom: 16
            }}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {pendingWalletChange?.contactName || "Contato"}
              </Typography>
            </Box>
          </Box>

          <Box mb={2}>
            <Typography variant="body1" style={{ marginBottom: 8, fontWeight: 500 }}>
              📋 Alteração:
            </Typography>
            <Box style={{ 
              backgroundColor: "#fff3e0", 
              padding: 12, 
              borderRadius: 4, 
              border: "1px solid #ffcc02"
            }}>
              <Typography variant="body2">
                <strong>De:</strong> {pendingWalletChange?.currentWallet?.name || "Nenhum atendente"}
              </Typography>
              <Typography variant="body2">
                <strong>Para:</strong> {pendingWalletChange?.newValue?.name || "Nenhum atendente"}
              </Typography>
            </Box>
          </Box>

          <Box style={{ 
            backgroundColor: "#ffebee", 
            padding: 12, 
            borderRadius: 4, 
            border: "1px solid #f44336"
          }}>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8, color: "#d32f2f" }}>
              ⚠️ Atenção:
            </Typography>
            {(() => {
              const isRemovingOwnWallet = pendingWalletChange?.currentWallet?.id === user.id && !pendingWalletChange?.newValue;
              const isChangingOwnWallet = pendingWalletChange?.currentWallet?.id === user.id && pendingWalletChange?.newValue;
              const isAdmin = user.profile === "admin" || user.profile === "super";
              
              if (isRemovingOwnWallet) {
                return (
                  <Typography variant="body2" style={{ color: "#d32f2f" }}>
                    • Você perderá acesso a este contato<br/>
                    • Apenas um administrador poderá vincular novamente
                  </Typography>
                );
              }
              
              if (isChangingOwnWallet) {
                return (
                  <Typography variant="body2" style={{ color: "#d32f2f" }}>
                    • Você perderá acesso a este contato<br/>
                    • Apenas {pendingWalletChange?.newValue?.name} ou um administrador poderão alterar novamente
                  </Typography>
                );
              }
              
              if (isAdmin && pendingWalletChange?.currentWallet) {
                return (
                  <Typography variant="body2" style={{ color: "#d32f2f" }}>
                    • O usuário {pendingWalletChange?.currentWallet?.name} perderá acesso a este contato<br/>
                    • {pendingWalletChange?.newValue ? `Apenas ${pendingWalletChange?.newValue?.name} ou um administrador` : "Apenas um administrador"} poderá alterar novamente
                  </Typography>
                );
              }
              
              return (
                <Typography variant="body2" style={{ color: "#d32f2f" }}>
                  • Esta ação alterará o responsável pelo contato<br/>
                  • {pendingWalletChange?.newValue ? `Apenas ${pendingWalletChange?.newValue?.name} ou um administrador` : "Apenas um administrador"} poderá alterar novamente
                </Typography>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button 
            onClick={handleCancelWalletChange} 
            variant="outlined"
            style={{ minWidth: 100 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmWalletChange} 
            variant="contained" 
            color="secondary"
            style={{ minWidth: 100 }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactModalNew;
